/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import * as React from 'react';

import { IEveNavigationMenuProps } from '../../definition-extensions/eve-navigation-menu.ext.props.autogenerated';
import { INavigationMenuViewProps } from '../../../../modules/eve-navigation-menu/eve-navigation-menu';

/**
 * INavigationMenuViewRootProps.
 */
export interface INavigationMenuViewRootProps {
    navProps: INavigationMenuViewProps & IEveNavigationMenuProps<{}>;
}

/**
 * Represent navigation menu state interface.
 */
export interface INavigationState {
    showQuickOrder: boolean;
    parentMenu?: number;
    activeMenu?: number;
    showMenu: boolean;
    activeMenuId: number;
    categoryImage?: Msdyn365.IImageData[] | null;
    mobileViewLabelText?: string;
    drawerKeyValue: Msdyn365.IDictionary<boolean>;
    isOnlyMobile: boolean;
    isNavOpen: boolean;
}

/**
 * NavMenuConstants enum.
 */
export enum NavMenuConstants {
    zero = 0,
    one = 1,
    two = 2,
    three = 3,
    four = 4,
    escapeKey = 27,
    rootMenu = 1
}

/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Get category image.
 * @param props - Navigation menu props.
 * @param categoryImage - Category Image.
 * @param alttext - AltText String.
 * @returns Returns Node.
 */
export const getCategoryImage = (props: INavigationMenuViewRootProps, categoryImage: string, alttext: string): React.ReactNode | null => {
    const categoryImageUrl = generateImageUrl(`${categoryImage}`, props.navProps.context.actionContext.requestContext.apiSettings);
    const defaultImageSettings: Msdyn365.IImageSettings = {
        viewports: {
            xs: { q: 'w=162&h=162&m=8', w: 0, h: 0 },
            sm: { q: 'w=162&h=162&m=8', w: 0, h: 0 },
            md: { q: 'w=203&h=203&m=8', w: 0, h: 0 },
            lg: { q: 'w=203&h=203&m=8', w: 0, h: 0 }
        },
        lazyload: true
    };
    if (categoryImageUrl !== undefined) {
        const imageData: Msdyn365.IImageData = { src: categoryImageUrl };
        return (
            <Msdyn365.Image
                requestContext={props.navProps.context.actionContext.requestContext}
                className='ms-nav-image__item'
                {...imageData}
                gridSettings={props.navProps.context.request.gridSettings!}
                imageSettings={props.navProps.config.categoryImageSettings ?? defaultImageSettings}
                loadFailureBehavior='hide'
                role='tabpanel'
                altText={alttext}
            />
        );
    }
    return null;
};
